import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import {DocumentNode} from 'graphql';
import React, {useEffect, useMemo, useState} from 'react';
import isEqual from 'react-fast-compare';
import {v4 as uuidv4} from 'uuid';

import {assert, assertAndContinue} from 'Asserts';
import {V2RequestsFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import {isInaccessibleInfo} from 'auth/RequestAccess';
import WorkspaceItemInaccessibleGuard from 'auth/WorkspaceItemInaccessibleGuard';
import BenchlingConfig from 'BenchlingConfig';
import * as BenchlingUtil from 'BenchlingUtil';
import {ActionMethod, ActionOrigin} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {SavingState} from 'constants/RegistryConstants';
import {EntitySummary} from 'constants/UserConstants';
import {isWorkflowCreateTaskFormsEnabled} from 'forms/FormsPermissions';
import {ProcessClientOrigin, ProcessExecutionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery, {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLArrayToREST} from 'graphql-client/declarative-translations/external/translate';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {SampleControlsFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevel';
import {hasMinimumSCRFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevelUtil';
import {LookupConfigSpec, SchemaById} from 'lookup-config/LookupConfigModalConstants';
import notifications from 'notifications';
import * as ProcessSchemaDAO from 'processes/dao/ProcessSchemaDAO';
import {ProcessesAllSchemasQuery} from 'processes/modals/utils/__generated/ProcessesAllSchemasQuery';
import {PROCESSES_ALL_SCHEMAS_QUERY} from 'processes/modals/utils/ProcessesAllSchemasQueryUtils';
import {ProcessStatusLifecyclesQuery} from 'processes/settings/__generated/ProcessStatusLifecyclesQuery';
import {
  ProcessTaskSchemaEditorQuery,
  ProcessTaskSchemaEditorQueryVariables,
} from 'processes/settings/__generated/ProcessTaskSchemaEditorQuery';
import CloneProcessTaskSchemaModal from 'processes/settings/CloneProcessTaskSchemaModal';
import ConfigureTaskFinderSection from 'processes/settings/ConfigureTaskFinderSection';
import CanOnlyBePartOfFlowchartField from 'processes/settings/fields/CanOnlyBePartOfFlowchartField';
import IsPropagateWatchersEnabledField from 'processes/settings/fields/IsPropagateWatchersEnabledField';
import ResponsiblePartyLookupConfigField from 'processes/settings/fields/ResponsiblePartyLookupConfigField';
import StatusLifecycleField from 'processes/settings/fields/StatusLifecycleField';
import GraphBuilderFlowchartConfigEditor from 'processes/settings/graph-builder-flowchart-config/GraphBuilderFlowchartConfigEditor';
import ProcessSchemaFieldTableRow from 'processes/settings/ProcessSchemaFieldTableRow';
import {
  DefaultCreationFolderSelectorContainer,
  DefaultEntryExecutionFolderSelectorContainer,
} from 'processes/settings/ProcessSchemaFolderSelectors';
import ProcessSchemaMetadataSectionRow, {
  NameField,
  OwnerField,
  PrefixField,
} from 'processes/settings/ProcessSchemaMetadataSectionRow';
import ProcessTaskGroupInstructionsField from 'processes/settings/ProcessTaskGroupInstructionsField';
import {
  cacheUpdatedTaskSchema,
  insertOrReplaceTaskSchema,
  isEmptyOrWhitespace,
} from 'processes/settings/ProcessTaskSchemaUtils';
import RemoveOutputSchemaModal from 'processes/settings/RemoveOutputSchemaModal';
import {canConfigureProcessTaskSchema} from 'processes/util/ProcessSchemaConfigUtil';
import {isExecutionBlockedByTemplatePublishing} from 'processes/util/ProcessTaskExecutionUtil';
import FieldTable, {ClientSchemaField, FieldTableLoading} from 'registry/field/FieldTable';
import {getNewOrUpdatedSnapshotFields, haveFieldsChanged} from 'registry/field/FieldUtil';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import NoOwnersSchemaMessage from 'schemas/NoOwnersSchemaMessage';
import SchemaSettingsExportDropdown from 'schemas/SchemaSettingsExportDropdown';
import {massageFieldsForSubmit} from 'schemas/SchemaUtil';
import SchemaWarehouseNameInput from 'schemas/SchemaWarehouseNameInput';
import {createSnapshotField, updateSnapshotField} from 'schemas/snapshot-field/SnapshotFieldDAO';
import {getSchemaOwnerChoices} from 'schemas/spec/SchemaSpecUtil';
import {
  filterSchemasByOrganizationJson,
  filterSchemasByRegistryOrganization,
} from 'schemas/util/FilterSchemasByOrganization';
import SettingsPageContainer, {SettingsPageContainerProps} from 'settings/SettingsPageContainer';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import InlineBanner, {InlineBannerType, InlineBannerActionType} from 'ui-library/banners/InlineBanner';
import FormGroup from 'ui-library/bs/FormGroup';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import * as Colors from 'ui-library/Colors';
import {MinimalEntity} from 'ui-library/entity-input/EntityInputConstants';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import PartiesField from 'ui-library/inputs/PartiesField';
import runModal from 'ui-library/runModal';
import ErrorList from 'ui-library/spec-editor/ErrorList';
import {Tab, TabContainer} from 'ui-library/TabContainer';
import licenses from 'user/Licenses';
import APIUtil from 'util/APIUtil';
import enumIncludes from 'util/enumIncludes';
import {withLoadingScreen} from 'util/Promises';
import strUtil from 'util/strUtil';
import QueryingValidationRulesTable from 'validation_rules/components/ValidationRulesTable';
import {FieldNamingException} from 'warehouse/nameToSqlIdentifier';

import {EntryTemplateForConfigureButton, isEntryTemplatePreCreate} from './ConfigureExecutionTemplateButton';
import ConfigureExecutionTemplateSection from './ConfigureExecutionTemplateSection';
import AutopopulateFromTasksField from './fields/AutopopulateFromTasksField';
import CanSetAssigneeOnTaskCreationField from './fields/CanSetAssigneeOnTaskCreationField';
import ExecutionTypeField from './fields/ExecutionTypeField';
import ProcessTaskSchemaFormsList from './ProcessTaskSchemaFormsList';
import ProcessTaskSchemaRequestV2DefinitionsList from './ProcessTaskSchemaRequestV2DefinitionsList';

type ProcessTaskSchema = ExtractByTypename<
  ProcessTaskSchemaEditorQuery['processTaskSchema'],
  'ProcessTaskSchemaNode'
>;

type ProcessOutputSchema = NonNullable<ProcessTaskSchema['outputSchema']>;

type FormDefinitionNode = ExtractByTypename<ProcessTaskSchema['relevantForms'][number], 'FormDefinitionNode'>;

type ProcessResponsibleParty = ProcessTaskSchema['responsibleParties'][number];

type ProcessResponsiblePartyLookupConfig = ProcessTaskSchema['responsiblePartyLookupConfigs'][number];

type TagSchemaGQL = ProcessesAllSchemasQuery['tagSchemas'][number];
type ResultSchemasGQL = ProcessesAllSchemasQuery['resultSchemas'][number];
type ProcessTaskStatusLifecycle = ProcessStatusLifecyclesQuery['processTaskStatusLifecycles'][number];
type PlateSchemaGQL = ProcessesAllSchemasQuery['plateSchemas'][number];
type LocationSchemaGQL = ProcessesAllSchemasQuery['locationSchemas'][number];
type ContainerSchemaGQL = ProcessesAllSchemasQuery['containerSchemas'][number];
type BoxSchemaGQL = ProcessesAllSchemasQuery['boxSchemas'][number];
type ProcessTaskSchemaGQL = ProcessesAllSchemasQuery['processTaskSchemasSupportingPlatformFeature'][number];

const SAVING_TASK_SCHEMA_MESSAGE = 'Saving...';
const CLONE_TASK_SCHEMA_DISABLED_TOOLTIP_MESSAGE =
  "Duplicating task schemas of 'Flowchart' execution type isn't supported at this time. You can duplicate task schemas of 'Direct' or 'Entry' execution type.";

export const PROCESS_TASK_SCHEMA_EDITOR_QUERY: DocumentNode = gql`
  query ProcessTaskSchemaEditorQuery($schemaId: StrictString!) @skipAudit @engComponent(name: WORKFLOWS) {
    processTaskSchema(id: $schemaId) {
      ... on ProcessTaskSchemaNode {
        ...ProcessTaskSchemaGraphQL
        relevantForms {
          ... on FormDefinitionNode {
            id
            isValid
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('ProcessTaskSchema').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const STATUS_LIFECYCLES_QUERY: DocumentNode = gql`
  query ProcessStatusLifecyclesQuery @skipAudit @engComponent(name: WORKFLOWS) {
    processTaskStatusLifecycles {
      ... on ProcessTaskStatusLifecycleNode {
        id
        name
        schemaExecutionType
      }
    }
  }
`;

type StorableSchemaGQL = BoxSchemaGQL | ContainerSchemaGQL | LocationSchemaGQL | PlateSchemaGQL;

const DEFAULT_TASK_SCHEMA_PREFIX = 'T';
const DEFAULT_OUTPUT_SCHEMA_PREFIX = 'O';

// Keep in sync with benchling/processes/api/process_task_schemas.py EXECUTION_TYPES_WITH_TASK_FINDER
export const EXECUTION_TYPES_WITH_TASK_FINDER = new Set([
  ProcessExecutionType.DIRECT,
  ProcessExecutionType.ENTRY,
]);

export enum ProcessTaskSchemaTabs {
  CONFIGURATION = 'configuration',
  FORMS = 'forms',
  VALIDATION_RULES = 'validation_rules',
  REQUEST_V2_DEFINITIONS = 'request_v2_definitions',
}

interface UpdateProcessSchema {
  taskGroupPrefix?: string;
  name?: string;
  sqlIdentifier?: string;
  taskSchemaPrefix?: string;
  outputSchemaPrefix?: string;
  taskFields?: Array<ClientSchemaField>;
  outputFields?: Array<ClientSchemaField>;
  autopopulateDirectOutputsFromTasks?: boolean;
  defaultResponsibleParties?: Array<MinimalEntity>;
  responsiblePartyLookupConfig?: LookupConfigSpec | null;
  taskFinderLookupConfig?: LookupConfigSpec | null;
  updateTaskFinder?: boolean;
  canSetAssigneeOnTaskCreation?: boolean;
  canOnlyBePartOfFlowchart?: boolean;
  defaultCreationFolderId?: string | null;
  defaultEntryExecutionFolderId?: string | null;
  taskGroupInstructions?: string | null;
  isPropagateWatchersEnabled?: boolean;
  flowchartConfig?: ProcessSchemaDAO.ProcessFlowchartConfigVersionSpec | null;
  configEditorEventGroupId?: string | null;
  didInitialFlowchartHaveChainedRouters?: boolean;
}

interface BaseProcessTaskSchemaEditorContainerProps {
  subtitle: string | null;
  processTaskSchema?: ProcessTaskSchema;
}

interface ProcessTaskSchemaEditorContainerProps extends BaseProcessTaskSchemaEditorContainerProps {
  children: React.ReactNode;
}

/** The same as ProcessTaskSchemaEditorContainerProps except it expects multiple children for each of the page's tabs */
interface ProcessTaskSchemaEditorTabbedContainerProps extends BaseProcessTaskSchemaEditorContainerProps {
  schemaId: string;
  configurationTab: React.ReactNode;
}

interface ProcessTaskSchemaEditorContentProps {
  processTaskSchema: ProcessTaskSchema | null;
  storableSchemas: Array<StorableSchemaGQL> | null;
  tagSchemas: Array<TagSchemaGQL> | null;
  taskSchemas: Array<ProcessTaskSchemaGQL> | null;
  statusLifecycles: Array<ProcessTaskStatusLifecycle> | null;
  refetchProcessSchema?: () => void;
  resultSchemas: Array<ResultSchemasGQL> | null;
  isAnyPartOfFlowchartConfigInaccessible: boolean | null;
}

interface ToggleOutputSchemaButtonProps {
  hasOutputSchema: boolean;
  onClick: () => void;
}

interface ProcessSchemaErrorListProps {
  error: string;
}

export interface ProcessSchemaFieldTableProps {
  hasBottomLink: boolean;
  schema: ProcessTaskSchema | ProcessOutputSchema | null;
  ownerUrlId: string;
  ownerId: string;
  fields: Array<ClientSchemaField>;
  onFieldsChange: (fields: Array<ClientSchemaField>) => void;
  schemaType: string;
  storableSchemas: Array<StorableSchemaGQL> | null;
  resultSchemas: Array<ResultSchemasGQL> | null;
  isDisabled?: boolean;
  dataTestAttrs?: DataTestAttrs;
  changeWarnings?: Array<JSX.Element>;
}

interface ProcessTaskSchemaEditorSectionProps {
  sectionTitle: string;
  sectionNumber: number;
  children?: React.ReactNode;
  isLastSection?: boolean;
}

interface InnerProcessTaskSchemaEditorPropsDataLoading {
  schemaId: string | null;
  storableSchemas: null;
  tagSchemas: null;
  taskSchemas: null;
  resultSchemas: null;
  statusLifecycles: null;
  isAnyPartOfFlowchartConfigInaccessible: null;
}

interface InnerProcessTaskSchemaEditorPropsWithData {
  schemaId: string | null;
  storableSchemas: Array<StorableSchemaGQL>;
  tagSchemas: Array<TagSchemaGQL>;
  taskSchemas: Array<ProcessTaskSchemaGQL>;
  resultSchemas: Array<ResultSchemasGQL>;
  statusLifecycles: Array<ProcessTaskStatusLifecycle>;
  isAnyPartOfFlowchartConfigInaccessible: boolean;
}

type InnerProcessTaskSchemaEditorProps =
  | InnerProcessTaskSchemaEditorPropsDataLoading
  | InnerProcessTaskSchemaEditorPropsWithData;

interface ProcessTaskSchemaEditorProps {
  schemaId: string | null;
}

// exported for test only
const baseTestAttrs = {component: 'ProcessTaskSchemaEditorQuery'};
export const DATA_TEST_ATTRS = {
  action: {...baseTestAttrs, element: 'action'},
  schemaError: {...baseTestAttrs, element: 'schemaError'},
};

export function getMinimalEntitiesFromResponsibleParties(
  parties: Array<ProcessResponsibleParty>
): Array<MinimalEntity> {
  return parties.map((party) => ({
    id: party.id,
    handle: party.handle,
  }));
}

function getResponsiblePartyLookupConfig(
  responsiblePartyLookupConfigs: Array<ProcessResponsiblePartyLookupConfig>
): LookupConfigSpec | null {
  assertAndContinue(
    responsiblePartyLookupConfigs.length <= 1,
    `Expected at most one responsiblePartyLookupConfig ${responsiblePartyLookupConfigs}`
  );
  return responsiblePartyLookupConfigs[0] ?? null;
}

function handleNavigateToSchemaListing(): void {
  BenchlingUtil.navigate(null, ProcessSchemaDAO.processTaskSchemasURL());
}

function makeProcessTaskSchemaSettingsPageContainerProps(
  props: BaseProcessTaskSchemaEditorContainerProps
): SettingsPageContainerProps {
  const isCloningDisabled = props.processTaskSchema?.executionType === ProcessExecutionType.FLOWCHART;
  return {
    onClickTitle: handleNavigateToSchemaListing,
    subtitle: `${props.subtitle ?? 'Create Task Schema'}`,
    title: 'Task Schemas',
    toolbar: props.processTaskSchema && licenses.hasEnterprisePlan() && (
      <div>
        <Tooltip
          disabled={!isCloningDisabled}
          placement={TooltipPlacement.BOTTOM}
          title={CLONE_TASK_SCHEMA_DISABLED_TOOLTIP_MESSAGE}
        >
          <Button
            className={css(styles.cloneButton)}
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            // Cloning a flowchart task schema is currently not allowed
            disabled={isCloningDisabled}
            size={ButtonSize.SMALL}
            text="Duplicate"
            onClick={() => {
              runModal(CloneProcessTaskSchemaModal, {
                originalProcessTaskSchema: props.processTaskSchema!,
                actionOrigin: ActionOrigin.SETTINGS_TASK_SCHEMA_PAGE,
                actionMethod: ActionMethod.BUTTON_CLICK,
              });
            }}
            dataTestAttrs={{component: 'ProcessTaskSchemaEditor', element: 'cloneTaskSchemaButton'}}
          />
        </Tooltip>
        <SchemaSettingsExportDropdown
          schemaId={props.processTaskSchema.id}
          schemaType={ModelTypeConstants.PROCESS_TASK_SCHEMA}
        />
      </div>
    ),
  };
}

/** A page wrapper that includes the Task Schema's name and some navigation shortcuts */
function ProcessTaskSchemaEditorContainer(props: ProcessTaskSchemaEditorContainerProps): JSX.Element {
  return (
    <SettingsPageContainer {...makeProcessTaskSchemaSettingsPageContainerProps(props)}>
      <div className="u-flex-grow u-min-width-zero">
        <div className="settingsPageContainer-section">{props.children}</div>
      </div>
    </SettingsPageContainer>
  );
}

/**
 * A page wrapper that includes the Task Schema's name and some navigation shortcuts, along with allowing multiple children for the various tabs
 */
function ProcessTaskSchemaEditorTabbedContainer(
  props: ProcessTaskSchemaEditorTabbedContainerProps
): JSX.Element {
  const [activeTab, setActiveTab] = useState<ProcessTaskSchemaTabs>(() => {
    const hashedTab = BenchlingUtil.getLocationHash();
    return enumIncludes(ProcessTaskSchemaTabs, hashedTab) ? hashedTab : ProcessTaskSchemaTabs.CONFIGURATION;
  });

  const onChangeTab = (tab: ProcessTaskSchemaTabs): void => {
    setActiveTab(tab);
    if (tab === ProcessTaskSchemaTabs.CONFIGURATION) {
      BenchlingUtil.clearLocationHash();
    } else {
      BenchlingUtil.setLocationHash(tab);
    }
  };

  return (
    <SettingsPageContainer {...makeProcessTaskSchemaSettingsPageContainerProps(props)}>
      <TabContainer<ProcessTaskSchemaTabs>
        activeTab={activeTab}
        onRequestTabChange={onChangeTab}
        className="u-flex-grow u-min-width-zero"
      >
        <Tab tabKey={ProcessTaskSchemaTabs.CONFIGURATION} title="Configuration">
          <div className="settingsPageContainer-section">{props.configurationTab}</div>
        </Tab>
        {isWorkflowCreateTaskFormsEnabled() && (
          <Tab tabKey={ProcessTaskSchemaTabs.FORMS} title="Forms">
            <div className="settingsPageContainer-section">
              <ProcessTaskSchemaFormsList
                schemaId={props.schemaId}
                processTaskSchema={props.processTaskSchema!}
              />
            </div>
          </Tab>
        )}
        {BenchlingConfig.VALIDATION_RULES_ENGINE_ENABLE_UI && (
          <Tab tabKey={ProcessTaskSchemaTabs.VALIDATION_RULES} title="Validation Rules">
            <div className="settingsPageContainer-section">
              <QueryingValidationRulesTable
                hasValidationRulesItemId={props.schemaId}
                onCreateValidationRules={ProcessSchemaDAO.createValidationRulesForProcessTaskSchema}
                onUpdateValidationRules={ProcessSchemaDAO.updateValidationRulesForProcessTaskSchema}
              />
            </div>
          </Tab>
        )}
        {BenchlingConfig.REQUESTS_V2_MVP_ENABLE &&
          isFeatureEnabled({feature: V2RequestsFeature.V2_REQUESTS_DISCOVER}) && (
            <Tab tabKey={ProcessTaskSchemaTabs.REQUEST_V2_DEFINITIONS} title="Request Templates">
              <div className="settingsPageContainer-section">
                <ProcessTaskSchemaRequestV2DefinitionsList taskSchemaId={props.schemaId} />
              </div>
            </Tab>
          )}
      </TabContainer>
    </SettingsPageContainer>
  );
}

function ProcessTaskSchemaEditorContent(props: ProcessTaskSchemaEditorContentProps): JSX.Element {
  const isBeingCreated = props.processTaskSchema == null;
  const isArchived = props.processTaskSchema?.archiveRecord != null;
  const [taskGroupPrefix, setTaskGroupPrefix] = useState(props.processTaskSchema?.taskGroupPrefix ?? '');
  const [processTaskSchemaName, setProcessTaskSchemaName] = useState(props.processTaskSchema?.name ?? '');
  const [processTaskSchemaSqlIdentifier, setProcessTaskSchemaSqlIdentifier] = useState(
    props.processTaskSchema?.sqlIdentifier
  );
  const [executionType, setExecutionType] = useState(
    props.processTaskSchema?.executionType ?? ProcessExecutionType.ENTRY
  );
  const [taskSchemaPrefix, setTaskSchemaPrefix] = useState(
    props.processTaskSchema?.prefix ?? DEFAULT_TASK_SCHEMA_PREFIX
  );
  const [outputSchemaPrefix, setOutputSchemaPrefix] = useState(
    props.processTaskSchema?.outputSchema?.prefix ?? DEFAULT_OUTPUT_SCHEMA_PREFIX
  );
  const [hasOutputSchema, setHasOutputSchema] = useState(
    props.processTaskSchema == null || props.processTaskSchema.outputSchema != null
  );
  const schemaOwnerChoices = getSchemaOwnerChoices();
  const [schemaOwner, setSchemaOwner] = useState<EntitySummary | null>(
    props.processTaskSchema?.organization ?? schemaOwnerChoices[0] ?? null
  );
  const [taskFields, setTaskFields] = useState<Array<ClientSchemaField>>(
    props.processTaskSchema?.allFields ?? []
  );
  const [outputFields, setOutputFields] = useState<Array<ClientSchemaField>>(
    props.processTaskSchema?.outputSchema?.allFields ?? []
  );
  const [canSetAssigneeOnTaskCreation, setCanSetAssigneeOnTaskCreation] = useState(
    props.processTaskSchema?.canSetAssigneeOnTaskCreation ?? true
  );
  const [isPropagateWatchersEnabled, setIsPropagateWatchersEnabled] = useState(
    props.processTaskSchema?.isPropagateWatchersEnabled
  );
  const [autopopulateDirectOutputsFromTasks, setAutopopulateDirectOutputsFromTasks] = useState(
    Boolean(props.processTaskSchema?.outputSchema?.directExecutionModalAutopopulateTaskIds)
  );
  const [canOnlyBePartOfFlowchart, setCanOnlyBePartOfFlowchart] = useState(
    props.processTaskSchema?.canOnlyBePartOfFlowchart ?? false
  );
  const [canConfigureFlowchart, setCanConfigureFlowchart] = useState(
    props.processTaskSchema?.executionType === ProcessExecutionType.FLOWCHART
  );
  const [validRelevantForms, setValidRelevantForms] = useState(
    props.processTaskSchema?.relevantForms?.filter(
      // either we know the form is valid or the form is inaccesible, and we have to assume it may be valid
      (formOrInaccessible) =>
        isInaccessibleInfo(formOrInaccessible) || (formOrInaccessible as FormDefinitionNode).isValid
    ) || []
  );

  // TODO(BNCH-20942): remove these state variables
  // The field table maintains internal state which does not get rolled back on failed updates.
  // Here we set a key on the field tables which is reset when a field update fails.
  // This triggers a re-mount of the field table component which brings it back into sync with the actual
  // state of the schema.
  const [taskFieldTableKey, setTaskFieldTableKey] = useState(uuidv4());
  const [outputFieldTableKey, setOutputFieldTableKey] = useState(uuidv4());
  const [flowchartConfigTableKey, setFlowchartConfigTableKey] = useState(uuidv4());
  const [isEditingFlowchart, setIsEditingFlowchart] = useState(false);

  const [errors, setErrors] = useState<{[errorKey: string]: string}>({});
  const [defaultResponsibleParties, setDefaultResponsibleParties] = useState<Array<MinimalEntity>>(
    getMinimalEntitiesFromResponsibleParties(props.processTaskSchema?.responsibleParties ?? [])
  );
  const [areDefaultResponsiblePartiesValid, setAreDefaultResponsiblePartiesValid] = useState(true);
  const [showDefaultResponsiblePartiesError, setShowDefaultResponsiblePartiesError] = useState(false);
  const [responsiblePartyLookupConfig, setResponsiblePartyLookupConfig] = useState<LookupConfigSpec | null>(
    getResponsiblePartyLookupConfig(props.processTaskSchema?.responsiblePartyLookupConfigs ?? [])
  );

  const [taskFinderLookupConfig, setTaskFinderLookupConfig] = useState<LookupConfigSpec | null>(
    props.processTaskSchema?.outputSchema?.taskFinderLookupConfig ?? null
  );
  const [executionTemplate, setExecutionTemplate] = useState<EntryTemplateForConfigureButton>(
    props.processTaskSchema?.entryTemplate ?? null
  );

  const [defaultCreationFolderId, setDefaultCreationFolderId] = useState<string | null>(
    props.processTaskSchema?.defaultCreationFolderOrInaccessible != null &&
      !isInaccessible(props.processTaskSchema.defaultCreationFolderOrInaccessible)
      ? props.processTaskSchema.defaultCreationFolderOrInaccessible.id
      : null
  );
  const [isDefaultCreationFolderInaccessible, setIsDefaultCreationFolderInaccessible] = useState(
    props.processTaskSchema?.defaultCreationFolderOrInaccessible != null &&
      isInaccessible(props.processTaskSchema.defaultCreationFolderOrInaccessible)
  );
  const [defaultEntryExecutionFolderId, setDefaultEntryExecutionFolderId] = useState<string | null>(
    props.processTaskSchema?.defaultEntryExecutionFolderOrInaccessible != null &&
      !isInaccessible(props.processTaskSchema.defaultEntryExecutionFolderOrInaccessible)
      ? props.processTaskSchema.defaultEntryExecutionFolderOrInaccessible.id
      : null
  );
  const [isDefaultEntryExecutionFolderInaccessible, setIsDefaultEntryExecutionFolderInaccessible] = useState(
    props.processTaskSchema?.defaultEntryExecutionFolderOrInaccessible != null &&
      isInaccessible(props.processTaskSchema.defaultEntryExecutionFolderOrInaccessible)
  );

  const [taskGroupInstructions, setTaskGroupInstructions] = useState(
    props.processTaskSchema?.taskGroupInstructions
  );

  const {statusLifecycles} = props;
  const statusLifecyclesForExecutionType = useMemo(() => {
    const lifecycles = (statusLifecycles ?? [])
      .filter(({schemaExecutionType}) => schemaExecutionType === executionType)
      .map((statusLifecycle) => ({...statusLifecycle, name: strUtil.toSentenceCase(statusLifecycle.name)}));
    assertAndContinue(
      statusLifecycles == null || lifecycles.length > 0,
      `Expected at least one status lifecycle for executionType ${executionType}`
    );
    return lifecycles;
  }, [statusLifecycles, executionType]);
  const [statusLifecycle, setStatusLifecycle] = useState<{id: string; name: string}>(
    props.processTaskSchema?.statusLifecycle ?? statusLifecyclesForExecutionType[0]
  );
  useEffect(() => {
    setStatusLifecycle(props.processTaskSchema?.statusLifecycle ?? statusLifecyclesForExecutionType[0]);
  }, [statusLifecyclesForExecutionType, props.processTaskSchema]);
  useEffect(() => {
    if (props.processTaskSchema?.statusLifecycle == null) {
      setStatusLifecycle(statusLifecyclesForExecutionType[0]);
    }
    if (executionType !== ProcessExecutionType.ENTRY) {
      setExecutionTemplate(null);
      setDefaultEntryExecutionFolderId(null);
    }
    // statusLifecyclesForExecutionType is updated when executionType updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executionType]);

  const {tagSchemas, taskSchemas, storableSchemas, resultSchemas} = props;
  const haveSchemasLoaded = [tagSchemas, taskSchemas, storableSchemas, resultSchemas].every(
    (arr) => arr != null
  );
  const allSchemasById = useMemo(() => {
    const schemasById = [...(tagSchemas ?? []), ...(storableSchemas ?? []), ...(resultSchemas ?? [])].reduce(
      (acc, schema) => {
        acc[schema.id] = {...schema, fields: schema.fieldsJson, allFields: schema.allFieldsJson};
        return acc;
      },
      {} as SchemaById
    );

    for (const taskSchema of taskSchemas ?? []) {
      schemasById[taskSchema.id] = taskSchema;
      if (taskSchema.outputSchema != null) {
        schemasById[taskSchema.outputSchema.id] = taskSchema.outputSchema;
      }
    }
    if (props.processTaskSchema != null) {
      schemasById[props.processTaskSchema.id] = props.processTaskSchema;
      if (props.processTaskSchema.outputSchema != null) {
        schemasById[props.processTaskSchema.outputSchema.id] = props.processTaskSchema.outputSchema;
      }
    }
    return schemasById;
  }, [tagSchemas, storableSchemas, resultSchemas, taskSchemas, props.processTaskSchema]);

  const toggleHasOutputSchemaButton = (): void => {
    if (hasOutputSchema) {
      runModal(RemoveOutputSchemaModal, {
        onRequestSubmit: () => {
          setHasOutputSchema(false);
          setOutputSchemaPrefix(DEFAULT_OUTPUT_SCHEMA_PREFIX);
          setOutputFields([]);
        },
      });
    } else {
      setHasOutputSchema(true);
    }
  };

  const onFieldsChanged = async (
    oldFields: Array<ClientSchemaField>,
    newFields: Array<ClientSchemaField>,
    fieldsKey: 'fields' | 'taskFields' | 'outputFields',
    setLocalFields: (fields: Array<ClientSchemaField>) => void
  ): Promise<void> => {
    let fieldsChanged;
    try {
      fieldsChanged = haveFieldsChanged(oldFields, newFields);
    } catch (e) {
      if (e instanceof FieldNamingException) {
        notifications.error(e.message);
        return;
      } else {
        throw e;
      }
    }

    if (!fieldsChanged) {
      return;
    }

    if (!isBeingCreated) {
      const newOrUpdatedSnapshotFields = getNewOrUpdatedSnapshotFields(oldFields, newFields);
      if (newOrUpdatedSnapshotFields.length > 0) {
        await createOrUpdateSnapshotField(newOrUpdatedSnapshotFields, newFields, fieldsKey);
      } else {
        await updateProcessTaskSchema({
          ...{[fieldsKey]: newFields},
        });
      }
    } else {
      setLocalFields(newFields);
    }
  };

  const validationErrorMessage = ((): string | null => {
    if (schemaOwner == null) {
      return 'This schema needs to have an owner.';
    }
    if (
      isEmptyOrWhitespace(taskGroupPrefix) ||
      isEmptyOrWhitespace(processTaskSchemaName) ||
      isEmptyOrWhitespace(taskSchemaPrefix)
    ) {
      return 'This schema needs to have prefixes and a schema name before it can be created.';
    }
    if (taskFields.length === 0) {
      return 'This schema needs to have Task fields before it can be created.';
    }
    if (hasOutputSchema) {
      if (isEmptyOrWhitespace(outputSchemaPrefix)) {
        return 'This schema needs to have an Output prefix before it can be created.';
      }
      if (outputFields.length === 0) {
        return 'This schema has an Output schema, so it needs to have Output fields before it can be created.';
      }
    }
    if (executionType === ProcessExecutionType.ENTRY && executionTemplate == null) {
      return 'This schema needs to have an Execution template before it can be created.';
    }
    return null;
  })();

  const createProcessTaskSchema = async (): Promise<void> => {
    setErrors({});
    if (!areDefaultResponsiblePartiesValid) {
      setShowDefaultResponsiblePartiesError(!areDefaultResponsiblePartiesValid);
      return;
    }
    // TODO: add default responsible parties to create payload
    const baseRequestData = {
      name: processTaskSchemaName,
      sqlIdentifier: processTaskSchemaSqlIdentifier,
      taskGroupPrefix,
      prefix: taskSchemaPrefix,
      executionType,
      clientOrigin: ProcessClientOrigin.PROCESSES,
      canSetAssigneeOnTaskCreation,
      schemaFields: massageFieldsForSubmit(taskFields),
      organizationId: schemaOwner!.id,
      defaultResponsiblePartiesIds: defaultResponsibleParties.map(({id}) => id),
      statusLifecycleId: statusLifecycle.id,
      defaultCreationFolderId,
      taskGroupInstructions,
      isPropagateWatchersEnabled,
      processOutputSchema: hasOutputSchema
        ? {
            prefix: outputSchemaPrefix,
            schemaFields: massageFieldsForSubmit(outputFields),
          }
        : null,
    };
    let requestData: ProcessSchemaDAO.CreateProcessTaskSchemaRequest;
    if (executionType === ProcessExecutionType.ENTRY) {
      assert(
        executionTemplate != null && isEntryTemplatePreCreate(executionTemplate),
        'Expected execution template to be configured for entry type schema'
      );
      requestData = {
        ...baseRequestData,
        executionType,
        executionTemplate: {
          templateCollectionId: executionTemplate?.templateCollectionId,
          templateName: executionTemplate?.name,
          sourceTemplateId: executionTemplate?.sourceTemplateId,
          publishOnSchemaCreation: executionTemplate?.publishOnSchemaCreation,
        },
        defaultEntryExecutionFolderId,
        canOnlyBePartOfFlowchart,
      };
    } else if (executionType === ProcessExecutionType.DIRECT) {
      requestData = {...baseRequestData, executionType, canOnlyBePartOfFlowchart};
      if (hasOutputSchema) {
        requestData.processOutputSchema!.autopopulateFromTasks = autopopulateDirectOutputsFromTasks;
      }
    } else if (executionType === ProcessExecutionType.FLOWCHART) {
      requestData = {...baseRequestData, executionType};
    } else {
      assert(false, `Unsupported execution type - ${executionType}`);
    }
    try {
      const processTaskSchema = await withLoadingScreen(
        ProcessSchemaDAO.createProcessTaskSchema(requestData),
        SAVING_TASK_SCHEMA_MESSAGE
      );
      cacheUpdatedTaskSchema(processTaskSchema);
      BenchlingUtil.navigate(null, ProcessSchemaDAO.processTaskSchemaURL(processTaskSchema.id));
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (e) {
      if (e.responseJSON) {
        setErrors(e.responseJSON.errors ?? {});
      }
      notifications.notifyError(e);
    }
  };

  const setStateOnUpdate = (params: UpdateProcessSchema & {updateTables?: boolean}): void => {
    if (params.taskGroupPrefix != null) {
      setTaskGroupPrefix(params.taskGroupPrefix);
    }
    if (params.name != null) {
      setProcessTaskSchemaName(params.name);
    }
    if (params.sqlIdentifier != null) {
      setProcessTaskSchemaSqlIdentifier(params.sqlIdentifier);
    }
    if (params.taskSchemaPrefix != null) {
      setTaskSchemaPrefix(params.taskSchemaPrefix);
    }
    if (params.taskFields != null) {
      setTaskFields(params.taskFields);
      // locally mark all forms as "invalidated" by clearing out the cached value from state
      setValidRelevantForms([]);
      if (params.updateTables) {
        setTaskFieldTableKey(uuidv4());
      }
    }
    if (params.outputSchemaPrefix != null) {
      setOutputSchemaPrefix(params.outputSchemaPrefix);
    }
    if (params.outputFields != null) {
      setOutputFields(params.outputFields);
      if (params.updateTables) {
        setOutputFieldTableKey(uuidv4());
      }
    }
    if (params.autopopulateDirectOutputsFromTasks != null) {
      setAutopopulateDirectOutputsFromTasks(params.autopopulateDirectOutputsFromTasks);
    }
    if (params.defaultResponsibleParties !== undefined) {
      setDefaultResponsibleParties(params.defaultResponsibleParties);
    }
    if (params.responsiblePartyLookupConfig !== undefined) {
      setResponsiblePartyLookupConfig(params.responsiblePartyLookupConfig);
    }
    if (params.updateTaskFinder) {
      setTaskFinderLookupConfig(params.taskFinderLookupConfig ?? null);
    }
    if (params.canSetAssigneeOnTaskCreation != null) {
      setCanSetAssigneeOnTaskCreation(params.canSetAssigneeOnTaskCreation);
    }
    if (params.canOnlyBePartOfFlowchart != null) {
      setCanOnlyBePartOfFlowchart(params.canOnlyBePartOfFlowchart);
    }
    if (params.defaultCreationFolderId !== undefined) {
      setDefaultCreationFolderId(params.defaultCreationFolderId);
    }
    if (params.defaultEntryExecutionFolderId !== undefined) {
      setDefaultEntryExecutionFolderId(params.defaultEntryExecutionFolderId);
    }
    if (params.taskGroupInstructions !== undefined) {
      setTaskGroupInstructions(params.taskGroupInstructions);
    }
    if (params.isPropagateWatchersEnabled != null) {
      setIsPropagateWatchersEnabled(params.isPropagateWatchersEnabled);
    }
    if (params.flowchartConfig !== undefined && params.updateTables) {
      setFlowchartConfigTableKey(uuidv4());
    }
  };

  const createOrUpdateSnapshotField = async (
    changedFields: Array<ClientSchemaField | SchemaField>,
    newFields: Array<ClientSchemaField>,
    fieldsKey: 'fields' | 'taskFields' | 'outputFields'
  ): Promise<void> => {
    setStateOnUpdate({
      [fieldsKey]: newFields,
    } as UpdateProcessSchema);
    assertAndContinue(changedFields.length === 1, 'Expected only one created/update snapshot field');
    const snapshotField = changedFields[0];
    const schemaId =
      fieldsKey === 'taskFields' ? props.processTaskSchema!.id : props.processTaskSchema!.outputSchema!.id;
    const saveSnapshotField = async (): Promise<void> => {
      try {
        if (!snapshotField.apiId) {
          await createSnapshotField({
            schemaId,
            displayName: snapshotField.displayName,
            systemName: snapshotField.systemName,
            lookupConfigSpec: snapshotField.lookupConfig!,
          });
        } else {
          await updateSnapshotField({
            snapshotFieldId: snapshotField.apiId,
            displayName: snapshotField.displayName,
            systemName: snapshotField.systemName,
            lookupConfigSpec: snapshotField.lookupConfig!,
            archiveRecord: snapshotField.archiveRecord,
          });
        }
        assert(props.refetchProcessSchema !== undefined, 'Expected defined refetchProcessSchema prop');
        props.refetchProcessSchema();
        setStateOnUpdate({
          taskFields: props.processTaskSchema!.allFields,
          outputFields: props.processTaskSchema!.outputSchema?.allFields ?? null,
        } as UpdateProcessSchema);
        notifications.success('Updated schema successfully');
        // eslint-disable-next-line benchlint/always-throw-from-catch
      } catch (e) {
        if (e.responseJSON != null) {
          setErrors(e.responseJSON.errors ?? {});
        }
        notifications.notifyError(e);
      }
    };

    await withLoadingScreen(saveSnapshotField());
  };

  const updateProcessTaskSchema = async (params: UpdateProcessSchema): Promise<void> => {
    // Optimistically update component state
    setStateOnUpdate(params);
    setErrors({});
    try {
      // TODO: add default responsible parties to update payload
      const processTaskSchema = await withLoadingScreen(
        ProcessSchemaDAO.updateProcessTaskSchema({
          schemaId: props.processTaskSchema!.id,
          ...(params.taskGroupPrefix != null ? {taskGroupPrefix: params.taskGroupPrefix} : {}),
          ...(params.name != null ? {name: params.name} : {}),
          ...(params.sqlIdentifier != null ? {sqlIdentifier: params.sqlIdentifier} : {}),
          ...(params.taskSchemaPrefix != null ? {prefix: params.taskSchemaPrefix} : {}),
          ...(params.canSetAssigneeOnTaskCreation != null
            ? {canSetAssigneeOnTaskCreation: params.canSetAssigneeOnTaskCreation}
            : {}),
          ...(params.canOnlyBePartOfFlowchart != null
            ? {canOnlyBePartOfFlowchart: params.canOnlyBePartOfFlowchart}
            : {}),
          defaultResponsiblePartiesIds:
            params.defaultResponsibleParties === undefined
              ? undefined
              : params.defaultResponsibleParties.map(({id}) => id),
          responsiblePartyLookupConfigs:
            params.responsiblePartyLookupConfig === undefined
              ? undefined
              : params.responsiblePartyLookupConfig == null
                ? null
                : [params.responsiblePartyLookupConfig],
          ...(params.taskFields != null ? {schemaFields: massageFieldsForSubmit(params.taskFields)} : {}),
          processOutputSchema: hasOutputSchema
            ? {
                ...(params.outputSchemaPrefix != null ? {prefix: params.outputSchemaPrefix} : {}),
                ...(params.outputFields != null
                  ? {schemaFields: massageFieldsForSubmit(params.outputFields)}
                  : {}),
                ...(params.updateTaskFinder ? {taskFinderLookupConfig: params.taskFinderLookupConfig} : {}),
                ...(params.autopopulateDirectOutputsFromTasks != null
                  ? {autopopulateFromTasks: params.autopopulateDirectOutputsFromTasks}
                  : {}),
              }
            : null,
          defaultCreationFolderId: params.defaultCreationFolderId,
          defaultEntryExecutionFolderId: params.defaultEntryExecutionFolderId,
          ...(params.flowchartConfig != null ? {flowchartConfig: params.flowchartConfig} : {}),
          taskGroupInstructions: params.taskGroupInstructions,
          ...(params.isPropagateWatchersEnabled != null
            ? {isPropagateWatchersEnabled: params.isPropagateWatchersEnabled}
            : {}),
          configEditorEventGroupId: params.configEditorEventGroupId,
          didInitialFlowchartHaveChainedRouters: params.didInitialFlowchartHaveChainedRouters ?? false,
          actionOrigin: ActionOrigin.SETTINGS_TASK_SCHEMA_PAGE,
        }),
        SAVING_TASK_SCHEMA_MESSAGE
      );
      cacheUpdatedTaskSchema(processTaskSchema);
      // Update state with field table from server on success
      setStateOnUpdate({
        taskFields: processTaskSchema.allFields,
        outputFields: processTaskSchema.outputSchema?.allFields ?? null,
      } as UpdateProcessSchema);
      notifications.success('Updated schema successfully');
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (e) {
      if (e.responseJSON != null) {
        setErrors(e.responseJSON.errors ?? {});
      }
      // Revert field state to match server on failure
      setStateOnUpdate({
        taskFields: props.processTaskSchema!.allFields,
        outputFields: props.processTaskSchema!.outputSchema?.allFields ?? null,
        updateTables: true,
      });
      // If the flowchart config was being updated we should put it back into editing mode.
      if (params.flowchartConfig != null) {
        setIsEditingFlowchart(true);
      }
      notifications.notifyError(e);
    }
  };

  // Only generate a prefix preview when the task group prefix is not empty
  const generatePrefixPreview = (label: string, prefix: string): string =>
    isEmptyOrWhitespace(taskGroupPrefix) ? '' : `${label}: ${taskGroupPrefix}1-${prefix}1`;

  const handleDefaultResponsiblePartiesUpdate = async (
    newDefaultResponsibleParties: Array<MinimalEntity>
  ): Promise<void> => {
    if (isEqual(newDefaultResponsibleParties, defaultResponsibleParties)) {
      return;
    }
    if (!isBeingCreated) {
      await updateProcessTaskSchema({
        defaultResponsibleParties: newDefaultResponsibleParties,
      });
    } else {
      setDefaultResponsibleParties(newDefaultResponsibleParties);
    }
  };

  const handleResponsiblePartyLookupConfigUpdate = async (
    newResponsiblePartyLookupConfig: LookupConfigSpec | null
  ): Promise<void> => {
    if (isEqual(newResponsiblePartyLookupConfig, responsiblePartyLookupConfig)) {
      return;
    }
    if (!isBeingCreated) {
      await updateProcessTaskSchema({
        responsiblePartyLookupConfig: newResponsiblePartyLookupConfig,
      });
    } else {
      setResponsiblePartyLookupConfig(newResponsiblePartyLookupConfig);
    }
  };

  const handleDefaultCreationFolderIdUpdate = async (
    newDefaultCreationFolderId: string | null
  ): Promise<void> => {
    if (newDefaultCreationFolderId === defaultCreationFolderId) {
      return;
    }
    if (!isBeingCreated) {
      await updateProcessTaskSchema({
        defaultCreationFolderId: newDefaultCreationFolderId,
      });
      setIsDefaultCreationFolderInaccessible(false);
    } else {
      setDefaultCreationFolderId(newDefaultCreationFolderId);
    }
  };

  const handleDefaultEntryExecutionFolderIdUpdate = async (
    newDefaultEntryExecutionFolderId: string | null
  ): Promise<void> => {
    if (newDefaultEntryExecutionFolderId === defaultEntryExecutionFolderId) {
      return;
    }
    if (!isBeingCreated) {
      await updateProcessTaskSchema({
        defaultEntryExecutionFolderId: newDefaultEntryExecutionFolderId,
      });
      setIsDefaultEntryExecutionFolderInaccessible(false);
    } else {
      setDefaultEntryExecutionFolderId(newDefaultEntryExecutionFolderId);
    }
  };

  const handleTaskGroupInstructionsUpdate = async (newTaskGroupInstructions: string): Promise<void> => {
    if (newTaskGroupInstructions === taskGroupInstructions) {
      return;
    }
    if (!isBeingCreated) {
      await updateProcessTaskSchema({
        taskGroupInstructions: newTaskGroupInstructions,
      });
    } else {
      setTaskGroupInstructions(newTaskGroupInstructions);
    }
  };

  if (schemaOwner == null) {
    return <NoOwnersSchemaMessage pluralName="workflow task schemas" />;
  }

  const getShowTemplateUnpublishedBanner = (): boolean => {
    if (isBeingCreated) {
      return false;
    } else {
      return isExecutionBlockedByTemplatePublishing(props.processTaskSchema!);
    }
  };

  return (
    <>
      {getShowTemplateUnpublishedBanner() && (
        <div className={css(styles.templatePublishingBanner)}>
          <InlineBanner
            bannerType={InlineBannerType.WARNING}
            text="The execution template for this task schema is not published"
            supportingInfo="Execution templates must be published so users can execute tasks created with this schema"
            action={{
              actionType: InlineBannerActionType.CUSTOM,
              actionText: 'Go to template',
              actionOnClick: () => {
                BenchlingUtil.navigate(null, props.processTaskSchema!.entryTemplate!.editUrl);
              },
            }}
          />
        </div>
      )}
      <ProcessTaskSchemaEditorSection sectionTitle="Task" sectionNumber={1}>
        <ProcessSchemaMetadataSectionRow>
          <PrefixField
            label="Task group prefix"
            value={taskGroupPrefix}
            error={errors.taskGroupPrefix ?? ''}
            isBeingCreated={isBeingCreated}
            isDisabled={
              isArchived ||
              isEditingFlowchart ||
              (!isBeingCreated && BenchlingConfig.WORKFLOWS_DISABLE_CHANGING_TASK_GROUP_PREFIX)
            }
            onChange={async (updatedTaskGroupPrefix: string) => {
              if (updatedTaskGroupPrefix === taskGroupPrefix) {
                return;
              }
              if (!isBeingCreated) {
                await updateProcessTaskSchema({
                  taskGroupPrefix: updatedTaskGroupPrefix,
                });
              } else {
                setTaskGroupPrefix(updatedTaskGroupPrefix);
              }
            }}
            dataTestAttrs={{component: 'ProcessTaskSchemaEditor', element: 'prefixField'}}
          />
          <NameField
            label="Name"
            value={processTaskSchemaName}
            error={errors.processTaskSchemaName ?? ''}
            isBeingCreated={isBeingCreated}
            isDisabled={isArchived || isEditingFlowchart}
            onChange={async (updatedName: string) => {
              if (updatedName === processTaskSchemaName) {
                return;
              }
              if (!isBeingCreated) {
                await updateProcessTaskSchema({
                  name: updatedName,
                });
              } else {
                setProcessTaskSchemaName(updatedName);
              }
            }}
            dataTestAttrs={{component: 'ProcessTaskSchemaEditor', element: 'nameField'}}
          />
          <OwnerField
            className={css(styles.ownerField)}
            schema={props.processTaskSchema}
            ownerChoices={schemaOwnerChoices}
            owner={schemaOwner}
            onOwnerChanged={setSchemaOwner}
            dataTestAttrs={{component: 'ProcessTaskSchemaEditor', element: 'ownerField'}}
          />
        </ProcessSchemaMetadataSectionRow>
        <ProcessSchemaMetadataSectionRow>
          <PrefixField
            label="ID prefix"
            value={taskSchemaPrefix}
            error={errors.taskSchemaPrefix ?? ''}
            isBeingCreated={isBeingCreated}
            isDisabled={isArchived || isEditingFlowchart}
            onChange={async (updatedTaskSchemaPrefix: string) => {
              if (updatedTaskSchemaPrefix === taskSchemaPrefix) {
                return;
              }
              if (!isBeingCreated) {
                await updateProcessTaskSchema({
                  taskSchemaPrefix: updatedTaskSchemaPrefix,
                });
              } else {
                setTaskSchemaPrefix(updatedTaskSchemaPrefix);
              }
            }}
            prefixPreview={generatePrefixPreview('Task ID preview', taskSchemaPrefix)}
            dataTestAttrs={{component: 'ProcessTaskSchemaEditor', element: 'taskPrefixField'}}
          />
          <ExecutionTypeField
            value={executionType}
            onChange={(newExecutionType: ProcessExecutionType) => {
              setExecutionType(newExecutionType);
              setCanConfigureFlowchart(newExecutionType === ProcessExecutionType.FLOWCHART);
            }}
            isBeingCreated={isBeingCreated}
          />
          {statusLifecycle ? (
            <StatusLifecycleField
              value={statusLifecycle}
              onChange={setStatusLifecycle}
              options={statusLifecyclesForExecutionType}
              isBeingCreated={isBeingCreated}
              error={errors.statusLifecycle}
            />
          ) : (
            <div className="fieldInput-label-container">
              <FieldLabel htmlFor="" label="Status lifecycle" />
              <div className="u-small-spacing-top">
                <HorizontalThrobber />
              </div>
            </div>
          )}
        </ProcessSchemaMetadataSectionRow>
        <ProcessSchemaMetadataSectionRow>
          <ConfigureExecutionTemplateSection
            executionType={executionType}
            executionTemplate={executionTemplate}
            onChange={(onTemplateChangeArgs) => {
              setExecutionTemplate({
                templateCollectionId: onTemplateChangeArgs.templateCollectionId,
                name: onTemplateChangeArgs.newTemplateName,
                sourceTemplateId: onTemplateChangeArgs.sourceTemplateId,
                publishOnSchemaCreation: onTemplateChangeArgs.publishOnSchemaCreation,
              });
            }}
            isDisabled={!isBeingCreated || isArchived}
            error={errors.sourceTemplate}
          />
          <DefaultCreationFolderSelectorContainer
            executionType={executionType}
            defaultCreationFolderId={defaultCreationFolderId}
            isInaccessible={isDefaultCreationFolderInaccessible}
            onChange={handleDefaultCreationFolderIdUpdate}
            isDisabled={isArchived || isEditingFlowchart}
          />
          <DefaultEntryExecutionFolderSelectorContainer
            executionType={executionType}
            defaultEntryExecutionFolderId={defaultEntryExecutionFolderId}
            isInaccessible={isDefaultEntryExecutionFolderInaccessible}
            onChange={handleDefaultEntryExecutionFolderIdUpdate}
            isDisabled={isArchived}
          />
        </ProcessSchemaMetadataSectionRow>
        <ProcessSchemaMetadataSectionRow>
          <PartiesField
            label="Default responsible parties"
            value={defaultResponsibleParties}
            onChange={handleDefaultResponsiblePartiesUpdate}
            isBeingCreated={isBeingCreated}
            isDisabled={isArchived || isEditingFlowchart}
            setDoesNotHaveUnresolvedInput={setAreDefaultResponsiblePartiesValid}
            showError={showDefaultResponsiblePartiesError}
            dataTestAttrs={{
              component: 'ProcessTaskSchemaEditor',
              element: 'defaultResponsiblePartiesField',
            }}
            errorText='Invalid "Default responsible parties" value'
          />
          <CanSetAssigneeOnTaskCreationField
            value={canSetAssigneeOnTaskCreation}
            disabled={isEditingFlowchart}
            hideWithMessage={
              executionType === ProcessExecutionType.FLOWCHART
                ? 'Flowchart tasks do not have assignees.'
                : null
            }
            onChange={async (value) => {
              if (value === canSetAssigneeOnTaskCreation) {
                return;
              }
              if (!isBeingCreated) {
                await updateProcessTaskSchema({
                  canSetAssigneeOnTaskCreation: value,
                });
              } else {
                setCanSetAssigneeOnTaskCreation(value);
              }
            }}
          />
          <CanOnlyBePartOfFlowchartField
            executionType={executionType}
            value={canOnlyBePartOfFlowchart}
            error={errors.canOnlyBePartOfFlowchart}
            disabled={isEditingFlowchart}
            onChange={async (value) => {
              if (value === canOnlyBePartOfFlowchart) {
                return;
              }
              if (!isBeingCreated) {
                await updateProcessTaskSchema({
                  canOnlyBePartOfFlowchart: value,
                });
              } else {
                setCanOnlyBePartOfFlowchart(value);
              }
            }}
          />
        </ProcessSchemaMetadataSectionRow>
        {hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V1) && (
          <ProcessSchemaMetadataSectionRow>
            <ResponsiblePartyLookupConfigField
              value={responsiblePartyLookupConfig}
              isBeingCreated={isBeingCreated}
              isDisabled={isArchived || isEditingFlowchart}
              isLoading={!haveSchemasLoaded}
              label="Task to responsible party lookup"
              onChange={handleResponsiblePartyLookupConfigUpdate}
              taskSchema={props.processTaskSchema}
              error={errors.responsiblePartyLookupConfigs}
            />
          </ProcessSchemaMetadataSectionRow>
        )}
        {BenchlingConfig.SCHEMAS_ENABLE_VIEW_SYSTEM_NAME &&
          BenchlingConfig.SCHEMAS_ENABLE_CHANGE_WAREHOUSE_NAME && (
            <ProcessSchemaMetadataSectionRow>
              <SchemaWarehouseNameInput
                isWritable={!isArchived && !isEditingFlowchart}
                schema={props.processTaskSchema}
                sqlIdentifier={processTaskSchemaSqlIdentifier ?? ''}
                onSqlIdentifierChange={async (updatedSqlIdentifier: string) => {
                  if (updatedSqlIdentifier === processTaskSchemaSqlIdentifier) {
                    return;
                  }
                  if (!isBeingCreated) {
                    await updateProcessTaskSchema({
                      sqlIdentifier: updatedSqlIdentifier,
                    });
                  } else {
                    setProcessTaskSchemaSqlIdentifier(updatedSqlIdentifier);
                  }
                }}
                error={errors.sqlIdentifier}
                dataTestAttrs={{component: 'ProcessTaskSchemaEditor', element: 'schemaWarehouseName'}}
              />
            </ProcessSchemaMetadataSectionRow>
          )}
        <ProcessSchemaMetadataSectionRow>
          <IsPropagateWatchersEnabledField
            value={isPropagateWatchersEnabled}
            onChange={async (value) => {
              if (value === isPropagateWatchersEnabled) {
                return;
              }
              if (!isBeingCreated) {
                await updateProcessTaskSchema({
                  isPropagateWatchersEnabled: value,
                });
              } else {
                setIsPropagateWatchersEnabled(value);
              }
            }}
            disabled={isArchived || isEditingFlowchart}
            executionType={executionType}
          />
        </ProcessSchemaMetadataSectionRow>
        <ProcessTaskGroupInstructionsField
          label="Instructions"
          value={taskGroupInstructions ?? ''}
          isBeingCreated={isBeingCreated}
          onChange={handleTaskGroupInstructionsUpdate}
          error={errors.taskGroupInstructions}
          isDisabled={isArchived || isEditingFlowchart}
          dataTestAttrs={{component: 'ProcessTaskSchemaEditor', element: 'taskGroupInstructionsField'}}
        />
        <ProcessSchemaErrorList error={errors.taskFields ?? ''} />
        <ProcessSchemaFieldTable
          isDisabled={isArchived || isEditingFlowchart}
          key={taskFieldTableKey}
          hasBottomLink={!hasOutputSchema}
          schema={props.processTaskSchema ?? null}
          ownerUrlId={schemaOwner.url_identifier}
          ownerId={schemaOwner.id}
          onFieldsChange={(updatedTaskFields) => {
            onFieldsChanged(taskFields, updatedTaskFields, 'taskFields', setTaskFields);
          }}
          schemaType="task"
          storableSchemas={props.storableSchemas}
          fields={taskFields}
          resultSchemas={props.resultSchemas}
          dataTestAttrs={{component: 'ProcessTaskSchemaEditor', element: 'taskTable'}}
          // if we edit the task fields, and there are any valid forms, warn the user that they are going to
          // invalidate forms with their actions
          changeWarnings={
            isWorkflowCreateTaskFormsEnabled() && !isBeingCreated && validRelevantForms.length
              ? [
                  <>
                    <InlineBanner
                      bannerType={InlineBannerType.WARNING}
                      text={`Updating this schema will have downstream effects on ${validRelevantForms.length} existing forms.`}
                      supportingInfo="This could cause data integrity issues if the affected form changes are not reviewed, acknowledged, and/or updated."
                    />
                    <p className="u-small-spacing-top">
                      Please contact the owner or responsible party for the corresponding forms that have been
                      affected.
                    </p>
                  </>,
                ]
              : undefined
          }
        />
      </ProcessTaskSchemaEditorSection>
      <ProcessTaskSchemaEditorSection
        isLastSection={!canConfigureFlowchart}
        sectionTitle="Output"
        sectionNumber={2}
      >
        {hasOutputSchema && (
          <>
            <ProcessSchemaMetadataSectionRow>
              <PrefixField
                label="ID prefix"
                value={outputSchemaPrefix}
                error={errors.outputSchemaPrefix ?? ''}
                isBeingCreated={isBeingCreated}
                isDisabled={isArchived || isEditingFlowchart}
                onChange={async (updatedOutputSchemaPrefix: string) => {
                  if (updatedOutputSchemaPrefix === outputSchemaPrefix) {
                    return;
                  }
                  if (!isBeingCreated) {
                    await updateProcessTaskSchema({
                      outputSchemaPrefix: updatedOutputSchemaPrefix,
                    });
                  } else {
                    setOutputSchemaPrefix(updatedOutputSchemaPrefix);
                  }
                }}
                prefixPreview={generatePrefixPreview('Output ID preview', outputSchemaPrefix)}
                dataTestAttrs={{component: 'ProcessTaskSchemaEditor', element: 'outputPrefixField'}}
              />
            </ProcessSchemaMetadataSectionRow>
            {executionType === ProcessExecutionType.DIRECT && (
              <ProcessSchemaMetadataSectionRow>
                <AutopopulateFromTasksField
                  value={autopopulateDirectOutputsFromTasks}
                  disabled={false}
                  onChange={async (value) => {
                    if (value === autopopulateDirectOutputsFromTasks) {
                      return;
                    }
                    if (!isBeingCreated) {
                      await updateProcessTaskSchema({
                        autopopulateDirectOutputsFromTasks: value,
                      });
                    } else {
                      setAutopopulateDirectOutputsFromTasks(value);
                    }
                  }}
                />
              </ProcessSchemaMetadataSectionRow>
            )}
            <ProcessSchemaErrorList error={errors.outputFields ?? ''} />
            <ProcessSchemaFieldTable
              isDisabled={isArchived || isEditingFlowchart}
              key={outputFieldTableKey}
              hasBottomLink={hasOutputSchema}
              schema={props.processTaskSchema?.outputSchema ?? null}
              ownerId={schemaOwner.id}
              ownerUrlId={schemaOwner.url_identifier}
              onFieldsChange={(updatedOutputFields) => {
                onFieldsChanged(outputFields, updatedOutputFields, 'outputFields', setOutputFields);
              }}
              schemaType="output"
              storableSchemas={props.storableSchemas}
              fields={outputFields}
              resultSchemas={props.resultSchemas}
              dataTestAttrs={{component: 'ProcessTaskSchemaEditor', element: 'outputTable'}}
            />
            {EXECUTION_TYPES_WITH_TASK_FINDER.has(executionType) && (
              <ConfigureTaskFinderSection
                isBeingCreated={isBeingCreated}
                isLoading={!haveSchemasLoaded}
                taskFinderLookupConfig={taskFinderLookupConfig}
                outputSchema={props.processTaskSchema?.outputSchema ?? null}
                taskSchema={props.processTaskSchema ?? null}
                isArchived={isArchived}
                error={errors.taskFinderLookupConfig}
                onChange={(updatedLookupConfig) => {
                  updateProcessTaskSchema({
                    taskFinderLookupConfig: updatedLookupConfig,
                    updateTaskFinder: true,
                  });
                }}
              />
            )}
          </>
        )}
        {isBeingCreated && (
          <ToggleOutputSchemaButton hasOutputSchema={hasOutputSchema} onClick={toggleHasOutputSchemaButton} />
        )}
        {!hasOutputSchema && !isBeingCreated && (
          <div className={css(styles.descriptionText)}>
            Output schema cannot be added after the task schema is created.
          </div>
        )}
      </ProcessTaskSchemaEditorSection>
      {canConfigureFlowchart && (
        <ProcessTaskSchemaEditorSection isLastSection={true} sectionTitle="Flowchart" sectionNumber={3}>
          {isBeingCreated ? (
            <span className={css(styles.descriptionText, styles.flowchartText)}>
              Configure the flowchart after the task schema is created.
            </span>
          ) : props.isAnyPartOfFlowchartConfigInaccessible ? (
            // TODO(BNCH-49564): Attempt to show this user a static view of the FC, even  if they can't edit it
            <ProcessSchemaErrorList error="You do not have access to all of the flowchart data.  Please contact your administrator for assistance." />
          ) : (
            <>
              <ProcessSchemaErrorList error={errors.flowchartConfig ?? ''} />
              <GraphBuilderFlowchartConfigEditor
                key={flowchartConfigTableKey}
                rootTaskSchema={props.processTaskSchema!}
                flowchartConfig={
                  props.processTaskSchema!.flowchartConfig?.currentFlowchartConfigVersion ?? null
                }
                allSchemasById={allSchemasById}
                allTaskSchemas={props.taskSchemas}
                isEditing={isEditingFlowchart}
                onSetIsEditing={setIsEditingFlowchart}
                onSubmit={(
                  flowchartConfig,
                  configEditorEventGroupId,
                  didInitialFlowchartHaveChainedRouters
                ) => {
                  updateProcessTaskSchema({
                    flowchartConfig,
                    configEditorEventGroupId,
                    didInitialFlowchartHaveChainedRouters,
                  });
                }}
              />
            </>
          )}
        </ProcessTaskSchemaEditorSection>
      )}
      {props.processTaskSchema == null && (
        <>
          <Button
            className={css(styles.createButton)}
            buttonStyle={ButtonStyle.SECONDARY}
            disabled={validationErrorMessage != null}
            onClick={createProcessTaskSchema}
            size={ButtonSize.MEDIUM}
            text="Create"
            dataTestAttrs={DATA_TEST_ATTRS.action}
          />
          {validationErrorMessage && (
            <div className={css(styles.schemaErrorMessage)} {...getTestProps(DATA_TEST_ATTRS.schemaError)}>
              <InlineBanner bannerType={InlineBannerType.WARNING} text={validationErrorMessage} />
            </div>
          )}
        </>
      )}
    </>
  );
}

function ToggleOutputSchemaButton(props: ToggleOutputSchemaButtonProps): JSX.Element {
  return (
    <div className={css(styles.outputSchemaToggle)}>
      <FormGroup className={classNames('u-no-spacing-bottom', css(styles.descriptionContents))}>
        <div className="settingsPageContainer-label">
          <Button
            buttonStyle={ButtonStyle.LINK}
            onClick={props.onClick}
            className={props.hasOutputSchema ? css(styles.removeText) : ''}
            dataTestAttrs={{
              component: 'ProcessTaskSchemaEditor',
              element: 'outputSchemaControlButton',
              key: props.hasOutputSchema ? 'remove' : 'add',
            }}
          >
            {props.hasOutputSchema ? 'Remove output schema' : 'Add output schema'}
          </Button>
        </div>
        <div className={css(styles.descriptionText)}>
          {`Output schema cannot be ${props.hasOutputSchema ? 'removed' : 'added'} after the task schema is
          created.`}
        </div>
      </FormGroup>
    </div>
  );
}

function ProcessSchemaErrorList(props: ProcessSchemaErrorListProps): JSX.Element {
  return <ErrorList errorMessage={props.error} />;
}

export function verifySchemaFields(fields: Array<ClientSchemaField>, schemaId?: string | null): boolean {
  if (schemaId) {
    const schemaType = APIUtil.apiIdToMaybeType(schemaId);
    if (
      schemaType === ModelTypeConstants.PROCESS_TASK_SCHEMA ||
      schemaType === ModelTypeConstants.PROCESS_OUTPUT_SCHEMA
    ) {
      const maxUnarchivedFieldsLimit = BenchlingConfig.WORKFLOWS_MAX_UNARCHIVED_FIELDS_PER_SCHEMA;
      if (maxUnarchivedFieldsLimit !== -1) {
        const unarchivedFieldCount = fields.filter((field) => field.archiveRecord == null).length;
        if (unarchivedFieldCount > maxUnarchivedFieldsLimit) {
          notifications.error(
            `You may not add more than ${maxUnarchivedFieldsLimit} unarchived fields on a ${schemaType === ModelTypeConstants.PROCESS_TASK_SCHEMA ? 'task' : 'output'} schema.`
          );
          return false;
        }
      }

      const maxSnapshotFieldsLimit = BenchlingConfig.WORKFLOWS_MAX_SNAPSHOT_FIELDS_PER_SCHEMA;
      if (maxSnapshotFieldsLimit !== -1) {
        const snapshotFieldCount = fields.filter((field) => field.isSnapshot).length;
        if (snapshotFieldCount > maxSnapshotFieldsLimit) {
          notifications.error(
            `You may not add more than ${maxSnapshotFieldsLimit} snapshot computed fields on a ${schemaType === ModelTypeConstants.PROCESS_TASK_SCHEMA ? 'task' : 'output'} schema.`
          );
          return false;
        }
      }
    }
  }
  return true;
}

export function ProcessSchemaFieldTable(props: ProcessSchemaFieldTableProps): JSX.Element {
  if (!(props.storableSchemas && props.resultSchemas)) {
    return (
      <div className={props.hasBottomLink ? css(styles.fieldTableWithLink) : css(styles.fieldTable)}>
        <FieldTableLoading schemaType={props.schemaType} />
      </div>
    );
  }

  const storableSchemas = filterSchemasByRegistryOrganization(props.storableSchemas, props.ownerId);
  const resultSchemas = filterSchemasByOrganizationJson(props.resultSchemas, props.ownerId);
  return (
    <div className={props.hasBottomLink ? css(styles.fieldTableWithLink) : css(styles.fieldTable)}>
      <FieldTable
        schemaId={props.schema?.id ?? null}
        schemaType={props.schemaType}
        isBeingCreated={props.schema == null}
        onFieldsChange={props.onFieldsChange}
        ownerUrlId={props.ownerUrlId}
        ownerId={props.ownerId}
        itemClass={ProcessSchemaFieldTableRow}
        fields={props.fields}
        storableSchemas={graphQLArrayToREST('StorableSchema', storableSchemas)}
        assaySchemas={graphQLArrayToREST('ResultSchema', resultSchemas)}
        isDisabled={Boolean(props.isDisabled)}
        shouldShowIsRequired={true}
        shouldShowIsIntegration={false}
        shouldShowIsParentLink={false}
        savingState={SavingState.COMPLETED}
        schema={props.schema}
        dataTestAttrs={props.dataTestAttrs}
        submissionWarnings={props.changeWarnings}
        archiveWarnings={props.changeWarnings}
        verifyFields={verifySchemaFields}
      />
    </div>
  );
}

const SectionNumber = ({sectionNumber}: {sectionNumber: number}): JSX.Element => (
  <div className={css(styles.schemaEditorSectionNumber)}>{sectionNumber}</div>
);

function ProcessTaskSchemaEditorSection(props: ProcessTaskSchemaEditorSectionProps): JSX.Element {
  return (
    <div className={css(styles.schemaEditorSection)}>
      <div className={css(styles.schemaEditorSectionHeader)}>
        <SectionNumber sectionNumber={props.sectionNumber} />
        <div className="legacy-font-proxima-14">{props.sectionTitle}</div>
      </div>
      <div
        className={css(
          styles.schemaEditorSectionChildren,
          !props.isLastSection && styles.schemaEditorSectionChildrenBorder
        )}
      >
        {props.children}
      </div>
    </div>
  );
}

const LoadingProcessTaskSchemaEditor = (): JSX.Element => (
  <ProcessTaskSchemaEditorContainer subtitle="">
    <HorizontalThrobber />
  </ProcessTaskSchemaEditorContainer>
);

function InnerProcessTaskSchemaEditor(props: {
  schemaId: string | null;
  processTaskSchema: ProcessTaskSchema | null;
  refetchProcessSchema?: () => void;
}): JSX.Element {
  const allSchemasResult = useBenchlingQuery<ProcessesAllSchemasQuery>(PROCESSES_ALL_SCHEMAS_QUERY);
  const statusLifecyclesResult = useBenchlingQuery<ProcessStatusLifecyclesQuery>(STATUS_LIFECYCLES_QUERY);
  // This calculation should guarantee that downstream components will never bump into an inaccessible schema.
  const isAnyPartOfFlowchartConfigInaccessible: boolean = (() => {
    const fcv = props.processTaskSchema?.flowchartConfig?.currentFlowchartConfigVersion;
    return fcv != null && fcv.availableSchemas.some((schema) => isInaccessible(schema));
  })();
  let innerProps: InnerProcessTaskSchemaEditorProps;

  if (
    allSchemasResult.type === QueryResultType.SUCCESS &&
    statusLifecyclesResult.type === QueryResultType.SUCCESS
  ) {
    innerProps = {
      schemaId: props.schemaId,
      storableSchemas: [
        ...allSchemasResult.data.boxSchemas,
        ...allSchemasResult.data.containerSchemas,
        ...allSchemasResult.data.locationSchemas,
        ...allSchemasResult.data.plateSchemas,
      ],
      tagSchemas: allSchemasResult.data.tagSchemas,
      taskSchemas:
        // Ensure that the "supported" task schemas list always contains the latest update to the task schema
        // being edited.
        props.processTaskSchema != null
          ? insertOrReplaceTaskSchema(
              allSchemasResult.data.processTaskSchemasSupportingPlatformFeature,
              props.processTaskSchema
            )
          : allSchemasResult.data.processTaskSchemasSupportingPlatformFeature,
      resultSchemas: allSchemasResult.data.resultSchemas,
      statusLifecycles: statusLifecyclesResult.data.processTaskStatusLifecycles,
      isAnyPartOfFlowchartConfigInaccessible,
    };
  } else {
    innerProps = {
      schemaId: props.schemaId,
      storableSchemas: null,
      tagSchemas: null,
      taskSchemas: null,
      resultSchemas: null,
      statusLifecycles: null,
      isAnyPartOfFlowchartConfigInaccessible: null,
    };
  }

  return (
    <ProcessTaskSchemaEditorContent
      processTaskSchema={props.processTaskSchema}
      refetchProcessSchema={props.refetchProcessSchema}
      {...innerProps}
    />
  );
}

function NonConfigurableSchema(): JSX.Element {
  return <div>This task schema is not user-configurable.</div>;
}

function shouldRenderTabbedContainer(): boolean {
  return (
    isWorkflowCreateTaskFormsEnabled() ||
    BenchlingConfig.VALIDATION_RULES_ENGINE_ENABLE_UI ||
    BenchlingConfig.REQUESTS_V2_MVP_ENABLE
  );
}

export default function ProcessTaskSchemaEditor(props: ProcessTaskSchemaEditorProps): JSX.Element {
  if (props.schemaId == null) {
    return (
      <ProcessTaskSchemaEditorContainer subtitle={null}>
        <InnerProcessTaskSchemaEditor schemaId={null} processTaskSchema={null} />
      </ProcessTaskSchemaEditorContainer>
    );
  }

  return (
    <BenchlingQuery<ProcessTaskSchemaEditorQuery, ProcessTaskSchemaEditorQueryVariables>
      query={PROCESS_TASK_SCHEMA_EDITOR_QUERY}
      renderLoading={LoadingProcessTaskSchemaEditor}
      variables={{schemaId: props.schemaId}}
      fetchPolicy="cache-and-network"
    >
      {(data, {refetch}) =>
        (() => {
          if (isInaccessible(data.processTaskSchema)) {
            return (
              <WorkspaceItemInaccessibleGuard
                inaccessibleInfo={data.processTaskSchema}
                modelType="processTaskSchema"
              />
            );
          }

          if (!canConfigureProcessTaskSchema(data.processTaskSchema)) {
            return <NonConfigurableSchema />;
          }

          const innerEditor = (
            <InnerProcessTaskSchemaEditor
              schemaId={props.schemaId}
              processTaskSchema={data.processTaskSchema}
              refetchProcessSchema={refetch}
            />
          );

          if (shouldRenderTabbedContainer()) {
            return (
              <ProcessTaskSchemaEditorTabbedContainer
                subtitle={data.processTaskSchema.name}
                processTaskSchema={data.processTaskSchema}
                configurationTab={innerEditor}
                schemaId={props.schemaId!}
              />
            );
          }
          return (
            <ProcessTaskSchemaEditorContainer
              subtitle={data.processTaskSchema.name}
              processTaskSchema={data.processTaskSchema}
            >
              {innerEditor}
            </ProcessTaskSchemaEditorContainer>
          );
        })()
      }
    </BenchlingQuery>
  );
}

export const styles = StyleSheet.create({
  schemaEditorSection: {
    display: 'block',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  schemaEditorSectionHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    flexDirection: 'row',
  },
  schemaEditorSectionNumber: {
    display: 'flex',
    alignItems: 'center',
    textAlign: 'center',
    fontSize: '12px',
    width: '20px',
    height: '20px',
    lineHeight: '20px',
    justifyContent: 'center',
    backgroundColor: Colors.WHITE,
    color: Colors.GRAY_DARKER,
    border: `1px solid ${Colors.GRAY_DARKER}`,
    borderRadius: '50%',
    boxShadow: `0 0 0 4px ${Colors.WHITE}`,
    marginRight: '12px',
  },
  schemaEditorSectionChildren: {
    position: 'relative',
    marginLeft: '10px',
    paddingLeft: '22px',
    paddingTop: '8px',
    marginTop: '8px',
    paddingBottom: '8px',
    marginBottom: '8px',
  },
  schemaEditorSectionChildrenBorder: {
    borderLeft: `1px solid ${Colors.GRAY_LIGHT}`,
  },
  schemaErrorMessage: {
    position: 'relative',
    margin: '8px 10px',
    padding: '8px 22px',
  },
  ownerField: {
    flex: '1',
  },
  fieldTable: {
    marginBottom: '32px',
  },
  fieldTableWithLink: {
    marginBottom: '18px',
  },
  outputSchemaToggle: {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: '32px',
  },
  createButton: {
    marginLeft: '32px',
  },
  descriptionContents: {
    flex: 0.5,
  },
  descriptionText: {
    color: Colors.GRAY_DARK,
    fontSize: '13px',
    lineHeight: '16px',
  },
  flowchartText: {
    display: 'inline-block',
    marginBottom: '12px',
  },
  removeText: {
    color: Colors.RED_LIGHT,
  },
  taskGroupInstructionEditorContainer: {
    marginBottom: '24px',
    fontSize: '12px',
  },
  cloneButton: {
    margin: 8,
  },
  templatePublishingBanner: {
    marginBottom: 16,
  },
});
